import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Zap, ArrowLeft, Loader2, Copy, Check, Sparkles, Link as LinkIcon,
  Save, Send, MessageSquare, ChevronDown, ChevronUp, Code, Mail, Calendar,
  ExternalLink, FileText, Bot, AlertTriangle, ArrowRight, CircleDollarSign,
  Paperclip, Upload, Download, Trash2, Eye, EyeOff, Package, UserPlus, X, Handshake,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { STATUSES, STATUS_LABELS, STATUS_COLORS, nextStatus } from "../lib/status";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => axios.create({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem("lf_session") || ""}` },
});

const EUR_RATE = 0.92;
const fmtPrice = (p, c) => c === "eur" ? `€${(p * EUR_RATE).toFixed(0)}` : `$${(p || 0).toFixed(0)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) { console.error("Clipboard copy failed:", err); }
  };
  return (
    <button data-testid="btn-copy" onClick={onCopy}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-zinc-900 text-white hover:bg-zinc-700 transition-colors">
      {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> {label}</>}
    </button>
  );
}

function CollapsibleCard({ title, icon: Icon, defaultOpen = false, badge, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testId} className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-zinc-50 transition-colors">
        <div className="flex items-center gap-3">
          {Icon && <Icon size={16} className="text-zinc-700" />}
          <span className="font-semibold text-zinc-900 text-sm">{title}</span>
          {badge && <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{badge}</span>}
        </div>
        {open ? <ChevronUp size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

function NotifyModal({ open, onClose, onConfirm, message }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" onClick={onClose} data-testid="notify-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
            <Mail size={18} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-bold text-zinc-900">Notify the requestor?</h3>
            <p className="text-sm text-zinc-600 mt-1">{message || "Send an email letting them know there's an update on their project page."}</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button data-testid="btn-skip-notify" onClick={() => onConfirm(false)} className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100 rounded-full">
            Save without email
          </button>
          <button data-testid="btn-confirm-notify" onClick={() => onConfirm(true)} className="px-5 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800 inline-flex items-center gap-2">
            <Mail size={14} /> Save & Notify
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatPanel({ projectId }) {
  const [messages, setMessages] = useState([]);
  const [filesByMsg, setFilesByMsg] = useState({});
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingFile, setPendingFile] = useState(null);
  const [fileVisible, setFileVisible] = useState(true);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const [m, f] = await Promise.all([
        axiosAuth().get(`${API}/admin/projects/${projectId}/messages`),
        axiosAuth().get(`${API}/admin/projects/${projectId}/files?category=chat_attachment`),
      ]);
      setMessages(m.data || []);
      const byMsg = {};
      (f.data || []).forEach(file => {
        const key = file.message_id || "_unattached";
        (byMsg[key] = byMsg[key] || []).push(file);
      });
      setFilesByMsg(byMsg);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if ((!content.trim() && !pendingFile) || sending) return;
    setSending(true);
    try {
      let messageId = null;
      if (content.trim()) {
        const res = await axiosAuth().post(`${API}/admin/projects/${projectId}/messages`, { content: content.trim() });
        messageId = res.data?.message_id;
      }
      if (pendingFile) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", pendingFile);
        fd.append("category", "chat_attachment");
        fd.append("visible_to_client", String(fileVisible));
        if (messageId) fd.append("message_id", messageId);
        await axiosAuth().post(`${API}/admin/projects/${projectId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setContent(""); setPendingFile(null); setFileVisible(true);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); setUploading(false); }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await axiosAuth().get(`${API}/admin/files/${fileId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Download failed"); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(zip|pdf)$/i.test(f.name)) { alert("Only .zip or .pdf files are allowed"); e.target.value = ""; return; }
    if (f.size > 25 * 1024 * 1024) { alert("File too large (max 25 MB)"); e.target.value = ""; return; }
    setPendingFile(f);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl flex flex-col" style={{ height: "640px" }} data-testid="admin-chat">
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2">
        <MessageSquare size={16} className="text-zinc-700" />
        <span className="font-semibold text-zinc-900 text-sm">Project Chat</span>
        <span className="ml-auto text-xs text-zinc-500">{messages.length} message{messages.length === 1 ? "" : "s"}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-zinc-50/50">
        {loading ? (
          <div className="flex items-center justify-center h-full"><Loader2 size={20} className="animate-spin text-zinc-400" /></div>
        ) : messages.length === 0 && !filesByMsg._unattached ? (
          <div className="text-center text-zinc-400 text-sm py-8">No messages yet — start the conversation.</div>
        ) : messages.map(m => {
          const attached = filesByMsg[m.message_id] || [];
          return (
            <div key={m.message_id} data-testid={`msg-${m.message_id}`}
              className={`max-w-[85%] ${m.sender === "admin" ? "ml-auto" : ""}`}>
              <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${m.sender === "admin" ? "text-right text-zinc-700" : "text-zinc-500"}`}>
                {m.sender_name} · {fmtDateTime(m.created_at)}
              </div>
              <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                m.sender === "admin" ? "bg-zinc-950 text-white rounded-br-sm" : "bg-white border border-zinc-200 text-zinc-800 rounded-bl-sm"
              }`}>{m.content}</div>
              {attached.map(f => (
                <button key={f.file_id} data-testid={`btn-msg-file-${f.file_id}`} onClick={() => downloadFile(f.file_id, f.filename)}
                  className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                    m.sender === "admin" ? "bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700" : "bg-white border-zinc-200 text-zinc-800 hover:bg-zinc-50"
                  }`}>
                  <Paperclip size={11} /> {f.filename}
                  {!f.visible_to_client && m.sender === "admin" && <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] text-amber-300"><EyeOff size={9} /> internal</span>}
                </button>
              ))}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="border-t border-zinc-100 p-3 space-y-2">
        {pendingFile && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs" data-testid="pending-file">
            <Paperclip size={12} className="text-blue-600" />
            <span className="font-semibold text-blue-900 truncate flex-1">{pendingFile.name}</span>
            <label className="inline-flex items-center gap-1 text-blue-800 cursor-pointer">
              <input type="checkbox" data-testid="input-file-visible" checked={fileVisible} onChange={e => setFileVisible(e.target.checked)} />
              Visible to client
            </label>
            <button data-testid="btn-clear-file" onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              className="text-blue-600 hover:text-blue-800"><X size={12} /></button>
          </div>
        )}
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".pdf,.zip" onChange={onPickFile} className="hidden" data-testid="input-file-upload" />
          <button data-testid="btn-attach-file" onClick={() => fileInputRef.current?.click()} disabled={sending}
            className="px-3 py-2 bg-zinc-100 text-zinc-700 rounded-xl hover:bg-zinc-200 disabled:opacity-40 self-end" title="Attach .zip or .pdf">
            <Paperclip size={14} />
          </button>
          <textarea data-testid="input-admin-chat" value={content} onChange={e => setContent(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
            placeholder="Reply to the client… (⌘/Ctrl+Enter to send)" rows={2}
            className="flex-1 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
          <button data-testid="btn-send-admin-chat" onClick={send} disabled={(!content.trim() && !pendingFile) || sending}
            className="px-4 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-xl hover:bg-zinc-800 disabled:opacity-40 self-end inline-flex items-center gap-1.5">
            {sending || uploading ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> Send</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChangeRequestsAdminCard({ projectId }) {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ amount: "", currency: "usd", days: "", response: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get(`${API}/admin/change-requests?project_id=${projectId}`);
      setReqs(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const startQuote = (r) => {
    setEditing(r.request_id);
    setForm({
      amount: r.estimated_amount_usd ? String(r.estimated_amount_usd) : "",
      currency: r.estimated_currency || "usd",
      days: r.estimated_delivery_days ? String(r.estimated_delivery_days) : "",
      response: r.admin_response || "",
    });
    setErr("");
  };

  const submit = async (requestId) => {
    const amt = parseFloat(form.amount);
    if (!(amt >= 0)) { setErr("Amount required"); return; }
    if (!form.response.trim()) { setErr("Please include a response note"); return; }
    setSaving(true); setErr("");
    try {
      await axiosAuth().post(`${API}/admin/change-requests/${requestId}/quote`, {
        admin_response: form.response.trim(),
        estimated_amount_usd: amt,
        estimated_currency: form.currency,
        estimated_delivery_days: form.days ? parseInt(form.days) : null,
      });
      setEditing(null);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!loading && reqs.length === 0) return null;

  return (
    <div className="bg-white border border-amber-200 rounded-2xl p-5" data-testid="admin-change-requests-card">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare size={16} className="text-amber-600" />
        <h3 className="font-semibold text-zinc-900 text-sm">Client change requests</h3>
        <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{reqs.length}</span>
      </div>
      {loading ? <div className="py-2 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
        : (
          <div className="space-y-3">
            {reqs.map((r) => (
              <div key={r.request_id} data-testid={`admin-cr-${r.request_id}`}
                className={`rounded-xl border p-3 ${
                  r.status === "approved" ? "bg-emerald-50 border-emerald-200" :
                  r.status === "declined" ? "bg-zinc-50 border-zinc-200 opacity-70" :
                  r.status === "quoted" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"
                }`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-bold text-zinc-950 text-sm flex-1 truncate">{r.title}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-900 text-white">{r.status.replace("_", " ")}</span>
                </div>
                <p className="text-xs text-zinc-700 whitespace-pre-wrap">{r.description}</p>
                {r.admin_response && (
                  <div className="mt-2 border-t pt-2 border-zinc-200/60">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Your quote</div>
                    <p className="text-xs text-zinc-800 whitespace-pre-wrap">{r.admin_response}</p>
                    {r.estimated_amount_usd != null && (
                      <div className="mt-1 text-xs text-zinc-700">
                        <strong>${r.estimated_amount_usd.toFixed(0)}</strong>
                        {r.estimated_delivery_days ? ` · ~${r.estimated_delivery_days} day(s)` : ""}
                      </div>
                    )}
                  </div>
                )}
                {editing === r.request_id ? (
                  <div className="mt-3 space-y-2 bg-white border border-amber-200 rounded-xl p-3" data-testid={`quote-form-${r.request_id}`}>
                    <div className="grid grid-cols-3 gap-2">
                      <input data-testid={`input-cr-amount-${r.request_id}`} type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        placeholder="Amount (USD equiv.)"
                        className="px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                      <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                        className="px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900">
                        <option value="usd">USD</option><option value="eur">EUR</option>
                      </select>
                      <input type="number" min="0" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })}
                        placeholder="Days added"
                        className="px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
                    </div>
                    <textarea rows={3} data-testid={`input-cr-response-${r.request_id}`} value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })}
                      placeholder="Short response shown to the client"
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
                    {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:bg-zinc-100 rounded-full">Cancel</button>
                      <button onClick={() => submit(r.request_id)} disabled={saving} data-testid={`btn-quote-cr-${r.request_id}`}
                        className="px-4 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5">
                        {saving ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send quote
                      </button>
                    </div>
                  </div>
                ) : (
                  r.status !== "approved" && r.status !== "declined" && (
                    <button onClick={() => startQuote(r)} data-testid={`btn-open-quote-${r.request_id}`}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800">
                      {r.status === "quoted" ? "Update quote" : "Respond with quote"}
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

function IntakePdfsCard({ projectId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get(`${API}/admin/projects/${projectId}/files?category=intake_pdf`);
      setFiles(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const download = async (f) => {
    try {
      const res = await axiosAuth().get(`${API}/admin/files/${f.file_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = f.filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Download failed"); }
  };

  const showExtracted = async (f) => {
    setLoadingPreview(true);
    try {
      const res = await axiosAuth().get(`${API}/admin/files/${f.file_id}/extracted-text`);
      setPreview({ ...res.data, source: f });
    } catch { alert("Could not load extracted text"); }
    finally { setLoadingPreview(false); }
  };

  if (!loading && files.length === 0) return null;

  return (
    <div className="bg-white border border-blue-200 rounded-2xl p-5" data-testid="intake-pdfs-card">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={16} className="text-blue-600" />
        <h3 className="font-semibold text-zinc-900 text-sm">Client-supplied intake PDFs</h3>
        <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{files.length}</span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">These documents were attached with the quote request. The AI read their contents when building this proposal.</p>
      {loading ? <div className="py-2 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
        : (
          <div className="space-y-2">
            {files.map(f => (
              <div key={f.file_id} data-testid={`intake-pdf-${f.file_id}`}
                className="rounded-xl p-3 border bg-blue-50/30 border-blue-200 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-zinc-950 truncate flex items-center gap-1.5">
                    <FileText size={12} /> {f.filename}
                  </div>
                  {f.description && <div className="text-xs text-zinc-600 mt-0.5">{f.description}</div>}
                  <div className="text-[10px] uppercase tracking-wider font-bold mt-1 text-zinc-400">
                    {(f.size_bytes / 1024).toFixed(0)} KB · {fmtDateTime(f.created_at)} · uploaded by {f.uploaded_by_name || "client"}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => showExtracted(f)} data-testid={`btn-show-extracted-${f.file_id}`}
                    className="p-1.5 text-zinc-700 hover:bg-zinc-100 rounded-lg" title="View extracted text">
                    <Bot size={13} />
                  </button>
                  <button onClick={() => download(f)} data-testid={`btn-download-intake-${f.file_id}`}
                    className="p-1.5 text-zinc-700 hover:bg-zinc-100 rounded-lg" title="Download PDF">
                    <Copy size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      }

      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={() => setPreview(null)} data-testid="extracted-text-modal">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-zinc-950 text-sm flex items-center gap-2">
                  <Bot size={14} /> AI-extracted text
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">{preview.filename} — {preview.extracted_chars.toLocaleString()} characters</p>
              </div>
              <button onClick={() => setPreview(null)} className="p-1 text-zinc-500 hover:bg-zinc-100 rounded-lg">✕</button>
            </div>
            <pre className="flex-1 overflow-y-auto bg-zinc-50 border border-zinc-200 rounded-xl p-3 text-xs font-mono whitespace-pre-wrap text-zinc-800 leading-relaxed" data-testid="extracted-text-body">
{preview.extracted_text || "(no extractable text — PDF may be scanned/image-based)"}
            </pre>
          </div>
        </div>
      )}
      {loadingPreview && <div className="fixed inset-0 bg-white/60 flex items-center justify-center z-50"><Loader2 size={24} className="animate-spin text-zinc-400" /></div>}
    </div>
  );
}

function DeliverablesCard({ projectId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: "", visible_to_client: true });
  const [pendingFile, setPendingFile] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get(`${API}/admin/projects/${projectId}/files?category=deliverable`);
      setFiles(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/\.(zip|pdf)$/i.test(f.name)) { alert("Only .zip or .pdf"); e.target.value = ""; return; }
    if (f.size > 25 * 1024 * 1024) { alert("Max 25 MB"); e.target.value = ""; return; }
    setPendingFile(f);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!pendingFile) { setErr("Please choose a .zip or .pdf file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("category", "deliverable");
      fd.append("visible_to_client", String(form.visible_to_client));
      if (form.description.trim()) fd.append("description", form.description.trim());
      await axiosAuth().post(`${API}/admin/projects/${projectId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ description: "", visible_to_client: true });
      setPendingFile(null); setShowForm(false);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e2) { setErr(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const toggleVisibility = async (file) => {
    try {
      await axiosAuth().patch(`${API}/admin/files/${file.file_id}`, { visible_to_client: !file.visible_to_client });
      await load();
    } catch { alert("Update failed"); }
  };

  const remove = async (file) => {
    if (!window.confirm(`Delete ${file.filename}?`)) return;
    try { await axiosAuth().delete(`${API}/admin/files/${file.file_id}`); await load(); } catch { alert("Delete failed"); }
  };

  const download = async (file) => {
    try {
      const res = await axiosAuth().get(`${API}/admin/files/${file.file_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = file.filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Download failed"); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="deliverables-admin-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h3 className="font-semibold text-zinc-900 text-sm flex items-center gap-2">
          <Package size={16} className="text-zinc-700" /> Deliverables & Notes
          {files.length > 0 && <span className="text-[10px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-full">{files.length}</span>}
        </h3>
        {!showForm && (
          <button data-testid="btn-add-deliverable" onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800">
            <Upload size={11} /> Upload deliverable
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-zinc-50 rounded-xl p-3 space-y-2 mb-3" data-testid="deliverable-form">
          <input ref={fileRef} type="file" accept=".pdf,.zip" onChange={onPick} data-testid="input-deliverable-file"
            className="block w-full text-xs text-zinc-700 file:mr-3 file:py-2 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-zinc-950 file:text-white hover:file:bg-zinc-800" />
          {pendingFile && <div className="text-xs text-zinc-600">Selected: <strong>{pendingFile.name}</strong> ({(pendingFile.size / 1024).toFixed(0)} KB)</div>}
          <textarea data-testid="input-deliverable-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Description (e.g. 'Final source code bundle')" rows={2}
            className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
          <label className="flex items-center gap-2 text-xs text-zinc-700">
            <input type="checkbox" data-testid="input-deliverable-visible" checked={form.visible_to_client} onChange={(e) => setForm({ ...form, visible_to_client: e.target.checked })} />
            Visible to client (uncheck for internal-only notes)
          </label>
          {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="deliverable-error">{err}</div>}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={uploading} data-testid="btn-submit-deliverable"
              className="px-4 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5">
              {uploading ? <Loader2 size={11} className="animate-spin" /> : <><Upload size={11} /> Upload</>}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setPendingFile(null); }} className="px-4 py-1.5 text-zinc-600 text-xs font-semibold rounded-full hover:bg-zinc-100">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
        : files.length === 0 ? <p className="text-xs text-zinc-500 py-2">No deliverables yet. Upload zips or PDFs to share with the client (or keep them internal-only).</p>
        : (
          <div className="space-y-2">
            {files.map(f => (
              <div key={f.file_id} data-testid={`deliverable-${f.file_id}`}
                className={`rounded-xl p-3 border flex items-start justify-between gap-3 ${f.visible_to_client ? "bg-emerald-50 border-emerald-200" : "bg-zinc-50 border-zinc-200"}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-zinc-950 truncate flex items-center gap-1.5">
                    <FileText size={12} /> {f.filename}
                  </div>
                  {f.description && <div className="text-xs text-zinc-700 mt-0.5">{f.description}</div>}
                  <div className="text-[10px] uppercase tracking-wider font-bold mt-1 inline-flex items-center gap-1">
                    {f.visible_to_client ? <span className="text-emerald-700 inline-flex items-center gap-1"><Eye size={9} /> Client visible</span> : <span className="text-zinc-500 inline-flex items-center gap-1"><EyeOff size={9} /> Internal only</span>}
                    <span className="text-zinc-400 ml-2">{(f.size_bytes / 1024).toFixed(0)} KB · {fmtDateTime(f.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => download(f)} data-testid={`btn-download-${f.file_id}`} className="p-1.5 text-zinc-700 hover:bg-zinc-100 rounded-lg" title="Download"><Download size={13} /></button>
                  <button onClick={() => toggleVisibility(f)} data-testid={`btn-toggle-${f.file_id}`} className="p-1.5 text-zinc-700 hover:bg-zinc-100 rounded-lg" title="Toggle visibility">
                    {f.visible_to_client ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button onClick={() => remove(f)} data-testid={`btn-delete-${f.file_id}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

function StartDateCard({ project, onChange }) {
  const [date, setDate] = useState(project.start_date ? project.start_date.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  // Determine if deposit is received and any contract-sensitive requirement met.
  const depositReceived = ["pending", "ai_magicians", "human_review", "pending_feedback", "applying_feedback", "final_review", "delivered"].includes(project.status);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await axiosAuth().patch(`${API}/admin/projects/${project.project_id}`, {
        start_date: date ? new Date(date).toISOString() : null,
      });
      setSaved(true); setTimeout(() => setSaved(false), 1500);
      onChange?.();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    setDate("");
    setSaving(true); setErr("");
    try {
      await axiosAuth().patch(`${API}/admin/projects/${project.project_id}`, { start_date: null });
      onChange?.();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="start-date-card">
      <div className="flex items-center gap-2 mb-3">
        <Calendar size={16} className="text-zinc-700" />
        <h3 className="font-semibold text-zinc-900 text-sm">Work Start Date</h3>
      </div>
      {!depositReceived && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-[11px] text-amber-900 flex items-start gap-2" data-testid="start-date-warning">
          <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
          <span>Deposit has not been received yet. Setting a start date before the 50% deposit + signed contract is discouraged — confirm before saving.</span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <input data-testid="input-start-date" type="date" value={date} onChange={e => setDate(e.target.value)}
          className="flex-1 min-w-[180px] px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
        <button data-testid="btn-save-start-date" onClick={save} disabled={saving || !date}
          className="px-4 py-2 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1.5">
          {saving ? <Loader2 size={11} className="animate-spin" /> : saved ? <><Check size={11} /> Saved</> : <><Save size={11} /> Save</>}
        </button>
        {project.start_date && (
          <button data-testid="btn-clear-start-date" onClick={clear} disabled={saving}
            className="px-3 py-2 text-zinc-600 text-xs font-semibold rounded-full hover:bg-zinc-100">Clear</button>
        )}
      </div>
      {err && <div className="text-[11px] text-red-600 mt-2">{err}</div>}
      {project.start_date && (
        <div className="text-xs text-zinc-600 mt-2" data-testid="start-date-display">
          Work scheduled to begin <strong>{fmtDate(project.start_date)}</strong>
        </div>
      )}
      <p className="text-[11px] text-zinc-500 mt-2">Visible to the client on their project page. Keep in sync with deposit + signed contract status.</p>
    </div>
  );
}

function AssignTeamCard({ project, onChange }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosAuth().get(`${API}/admin/admins`);
        if (!cancelled) setAdmins(res.data || []);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const assign = async (userId) => {
    setSaving(true);
    try {
      await axiosAuth().patch(`${API}/admin/projects/${project.project_id}/assign`, { user_id: userId || null });
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || "Failed to assign"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="assign-team-card">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus size={16} className="text-zinc-700" />
        <h3 className="font-semibold text-zinc-900 text-sm">Assigned Team Member</h3>
      </div>
      {loading ? <div className="py-2 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
        : (
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="select-assignee" value={project.assigned_to || ""} onChange={(e) => assign(e.target.value)} disabled={saving}
              className="flex-1 min-w-[200px] px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 disabled:opacity-50">
              <option value="">— Unassigned —</option>
              {admins.map(a => <option key={a.user_id} value={a.user_id}>{a.name} ({a.email})</option>)}
            </select>
            {saving && <Loader2 size={14} className="animate-spin text-zinc-400" />}
          </div>
        )
      }
      {project.assigned_to && (
        <div className="mt-2 text-xs text-zinc-600" data-testid="assignee-display">
          Assigned to: <strong>{project.assigned_to_name}</strong> ({project.assigned_to_email})
        </div>
      )}
    </div>
  );
}

function AdditionalChargesAdminCard({ projectId, currency, onChange }) {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: "", amount_usd: "", currency: currency || "usd", payment_required_before_delivery: true });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get(`${API}/admin/projects/${projectId}/charges`);
      setCharges(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.description.trim() || !form.amount_usd) { setErr("Description and amount are required"); return; }
    setSubmitting(true);
    try {
      await axiosAuth().post(`${API}/admin/projects/${projectId}/charges`, {
        description: form.description.trim(),
        amount_usd: parseFloat(form.amount_usd),
        currency: form.currency,
        payment_required_before_delivery: form.payment_required_before_delivery,
        origin_url: window.location.origin,
      });
      setForm({ description: "", amount_usd: "", currency: currency || "usd", payment_required_before_delivery: true });
      setShowForm(false);
      await load();
      onChange?.();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed to add charge"); }
    finally { setSubmitting(false); }
  };

  const cancelCharge = async (chargeId) => {
    if (!window.confirm("Cancel this charge? The Stripe payment link will become invalid.")) return;
    try { await axiosAuth().post(`${API}/admin/projects/${projectId}/charges/${chargeId}/cancel`); await load(); onChange?.(); }
    catch (e2) { alert(e2.response?.data?.detail || "Failed to cancel"); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="charges-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h3 className="font-semibold text-zinc-900 text-sm flex items-center gap-2">
          <CircleDollarSign size={16} className="text-zinc-700" /> Additional Charges
          {charges.length > 0 && <span className="text-[10px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-full">{charges.length}</span>}
        </h3>
        {!showForm && (
          <button data-testid="btn-add-charge" onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800">
            + Add charge
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="space-y-2 bg-zinc-50 rounded-xl p-3 mb-3" data-testid="add-charge-form">
          <textarea data-testid="input-charge-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What's the additional scope or change?" rows={2}
            className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
          <div className="flex gap-2">
            <input data-testid="input-charge-amount" type="number" step="0.01" min="0" value={form.amount_usd} onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
              placeholder="Amount (USD)"
              className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            <select data-testid="select-charge-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none">
              <option value="usd">USD</option><option value="eur">EUR</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-700">
            <input type="checkbox" data-testid="input-charge-required" checked={form.payment_required_before_delivery} onChange={(e) => setForm({ ...form, payment_required_before_delivery: e.target.checked })} />
            Required before delivery
          </label>
          {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="charge-error">{err}</div>}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={submitting} data-testid="btn-submit-charge"
              className="px-4 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5">
              {submitting ? <Loader2 size={11} className="animate-spin" /> : "Create charge & email client"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-1.5 text-zinc-600 text-xs font-semibold rounded-full hover:bg-zinc-100">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
        : charges.length === 0 ? <p className="text-xs text-zinc-500 py-2">No additional charges yet.</p>
        : (
          <div className="space-y-2">
            {charges.map(c => (
              <div key={c.charge_id} data-testid={`admin-charge-row-${c.charge_id}`} className={`rounded-xl p-3 border flex items-start justify-between gap-3 ${c.status === "paid" ? "bg-emerald-50 border-emerald-200" : c.status === "cancelled" ? "bg-zinc-50 border-zinc-200 opacity-60" : "bg-amber-50 border-amber-200"}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-zinc-950">${c.amount_usd?.toFixed(0)} <span className="text-xs uppercase font-normal text-zinc-500">{c.currency}</span></div>
                  <div className="text-xs text-zinc-700 mt-0.5">{c.description}</div>
                  <div className="text-[10px] uppercase tracking-wider font-bold mt-1">
                    {c.status === "paid" ? <span className="text-emerald-700">Paid</span> : c.status === "cancelled" ? <span className="text-zinc-500">Cancelled</span> : <span className="text-amber-800">Pending</span>}
                  </div>
                </div>
                {c.status === "pending" && (
                  <div className="flex flex-col gap-1">
                    <a href={c.payment_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline inline-flex items-center gap-1"><ExternalLink size={10} />Stripe</a>
                    <button onClick={() => cancelCharge(c.charge_id)} data-testid={`btn-cancel-charge-${c.charge_id}`} className="text-[10px] text-red-600 hover:underline">Cancel</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

function QuoteVersionsCard({ projectId, currentProposalId, onRegenerated }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get(`${API}/admin/projects/${projectId}/quotes`);
      setQuotes(res.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, currentProposalId]);

  const regenerate = async () => {
    if (!window.confirm("Generate a new quote version? The previous quote will be archived but remain accessible.")) return;
    setRegenerating(true);
    try {
      await axiosAuth().post(`${API}/admin/projects/${projectId}/regenerate-proposal`);
      await load();
      onRegenerated?.();
    } catch (e) { alert(e.response?.data?.detail || "Failed to regenerate"); }
    finally { setRegenerating(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="quotes-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h3 className="font-semibold text-zinc-900 text-sm flex items-center gap-2">
          <FileText size={16} className="text-zinc-700" /> Quote Versions
          {quotes.length > 0 && <span className="text-[10px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-full">{quotes.length}</span>}
        </h3>
        <button data-testid="btn-regenerate-quote" onClick={regenerate} disabled={regenerating}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-full hover:bg-purple-700 disabled:opacity-50">
          {regenerating ? <><Loader2 size={11} className="animate-spin" /> Regenerating…</> : <><Sparkles size={11} /> Regenerate Quote</>}
        </button>
      </div>
      {loading ? <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
        : quotes.length === 0 ? <p className="text-xs text-zinc-500 py-2">No quotes yet.</p>
        : (
          <div className="space-y-1.5">
            {quotes.map((q, i) => (
              <div key={q.proposal_id} data-testid={`quote-version-${q.proposal_id}`}
                className={`rounded-xl p-3 border flex items-center justify-between gap-3 text-xs ${q.is_active ? "bg-emerald-50 border-emerald-200" : "bg-zinc-50 border-zinc-200"}`}>
                <div>
                  <span className="font-bold text-zinc-950">v{q.version || "—"}</span>
                  {q.is_active && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[9px] uppercase font-bold tracking-wider">Active</span>}
                  <span className="ml-2 text-zinc-500">{new Date(q.created_at).toLocaleString()}</span>
                </div>
                <div className="font-bold text-zinc-950">${q.delivery_adjusted_price_usd?.toFixed(0)}</div>
              </div>
            ))}
          </div>
        )
      }
      <p className="text-[11px] text-zinc-500 mt-3">Once generated, quotes are immutable. Regenerating creates a new version; previous versions remain on file.</p>
    </div>
  );
}

export default function AdminProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Preview URL state
  const [previewUrl, setPreviewUrl] = useState("");
  const [pendingPreview, setPendingPreview] = useState(null);
  const [savingPreview, setSavingPreview] = useState(false);
  const [showNotify, setShowNotify] = useState(false);

  // Emergent prompt state
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");

  const load = async () => {
    try {
      const res = await axiosAuth().get(`${API}/admin/projects/${projectId}`);
      setProject(res.data);
      setPreviewUrl(res.data?.preview_url || "");
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
      else setError(e.response?.data?.detail || "Failed to load project");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const proposal = project?.proposal;
  const proposalPrice = project?.price_override || proposal?.delivery_adjusted_price_usd;

  const handleSavePreview = () => {
    setPendingPreview(previewUrl.trim());
    setShowNotify(true);
  };

  const confirmSavePreview = async (notify) => {
    setShowNotify(false);
    setSavingPreview(true);
    try {
      await axiosAuth().patch(`${API}/admin/projects/${projectId}/preview-url`, {
        preview_url: pendingPreview, notify,
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingPreview(false);
      setPendingPreview(null);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError("");
    try {
      await axiosAuth().post(`${API}/admin/projects/${projectId}/generate-emergent-prompt`);
      await load();
    } catch (e) {
      setGenError(e.response?.data?.detail || "Failed to generate prompt");
    } finally { setGenerating(false); }
  };

  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");
  const changeStatus = async (newStatus) => {
    if (!newStatus || statusSaving) return;
    setStatusSaving(true);
    setStatusError("");
    try {
      await axiosAuth().patch(`${API}/admin/projects/${projectId}`, { status: newStatus });
      await load();
    } catch (e) {
      setStatusError(e.response?.data?.detail || "Failed to change status");
    } finally { setStatusSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-project-detail">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate("/admin/dashboard")} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="btn-back"><ArrowLeft size={16} /></button>
        <BrandLogo variant="mark" size={32} />
        <div className="space-y-1.5"><div className="h-3 w-32 bg-zinc-200 rounded animate-pulse" /><div className="h-2.5 w-48 bg-zinc-100 rounded animate-pulse" /></div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {[0,1,2].map(i => <div key={i} className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-3"><div className="h-4 w-1/3 bg-zinc-200 rounded animate-pulse" /><div className="h-3 w-2/3 bg-zinc-100 rounded animate-pulse" /><div className="h-3 w-full bg-zinc-100 rounded animate-pulse" /></div>)}
        </div>
        <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-2xl p-5 space-y-3" style={{ height: "560px" }}>
          <div className="h-4 w-1/2 bg-zinc-200 rounded animate-pulse" />
          <div className="h-3 w-full bg-zinc-100 rounded animate-pulse" />
          <div className="flex justify-center pt-12"><Loader2 size={20} className="animate-spin text-zinc-400" /></div>
        </div>
      </div>
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
      <div className="text-center"><AlertTriangle size={28} className="text-red-500 mx-auto mb-3" /><p className="text-sm text-zinc-700">{error}</p>
        <Link to="/admin/dashboard" className="mt-4 inline-block text-sm text-blue-600 underline">Back to Dashboard</Link></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-project-detail">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/dashboard")} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="btn-back">
            <ArrowLeft size={16} />
          </button>
          <BrandLogo variant="mark" size={32} />
          <div>
            <div className="font-bold text-zinc-900 text-sm">{project.project_id}</div>
            <div className="text-xs text-zinc-500">{project.client_name} · {project.client_email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span className={`inline-flex px-2.5 py-1 rounded-full font-semibold border ${STATUS_COLORS[project.status] || STATUS_COLORS.pending}`} data-testid="status-badge">{STATUS_LABELS[project.status] || project.status}</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Project header */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h1 className="text-xl font-extrabold text-zinc-950">{proposal?.project_summary?.split(".")[0] || project.idea_description.slice(0, 80)}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1"><FileText size={11} />{project.industry}</span>
                  <span className="inline-flex items-center gap-1"><Calendar size={11} />Created {fmtDate(project.created_at)}</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><Calendar size={11} />Due {fmtDate(project.due_date)}</span>
                  {project.promoter_code && (
                    <Link to="/admin/promoters"
                      data-testid="project-promoter-chip"
                      title="Referred by a promoter — +7% markup baked into the quote"
                      className="inline-flex items-center gap-1 font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full transition-colors">
                      <Handshake size={11} /> Promoter: {project.promoter_code}
                    </Link>
                  )}
                </div>
              </div>
              {proposalPrice && <div className="text-right"><div className="text-xs text-zinc-500">Price</div><div className="text-2xl font-extrabold text-zinc-950">{fmtPrice(proposalPrice, project.currency)}</div></div>}
            </div>
            <div className="bg-zinc-50 rounded-xl p-3 text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">{project.idea_description}</div>
          </div>

          {/* Status pipeline */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="status-manager-card">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-2">
                <CircleDollarSign size={16} className="text-zinc-700" />
                <h3 className="font-semibold text-zinc-900 text-sm">Build Stage</h3>
              </div>
              <div className="flex items-center gap-2">
                <select data-testid="select-project-status" value={project.status} disabled={statusSaving}
                  onChange={(e) => changeStatus(e.target.value)}
                  className="px-3 py-1.5 text-xs border border-zinc-200 rounded-full bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-900 disabled:opacity-50">
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
                {nextStatus(project.status) && (
                  <button data-testid="btn-advance-status" onClick={() => changeStatus(nextStatus(project.status))} disabled={statusSaving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800 disabled:opacity-50">
                    {statusSaving ? <Loader2 size={12} className="animate-spin" /> : <><ArrowRight size={12} /> Advance to {STATUS_LABELS[nextStatus(project.status)]}</>}
                  </button>
                )}
              </div>
            </div>
            {statusError && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3" data-testid="status-error">{statusError}</div>}

            {/* Final review deadline banner */}
            {project.status === "final_review" && project.final_review_deadline && (() => {
              const deadline = new Date(project.final_review_deadline);
              const ms = deadline - new Date();
              const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
              const overdue = ms < 0;
              return (
                <div data-testid="final-review-banner" className={`rounded-xl p-4 mb-3 border ${overdue ? "bg-red-50 border-red-200" : "bg-rose-50 border-rose-200"}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={16} className={overdue ? "text-red-600 mt-0.5" : "text-rose-600 mt-0.5"} />
                    <div className="flex-1">
                      <div className={`font-bold text-sm ${overdue ? "text-red-900" : "text-rose-900"}`}>
                        {overdue ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}` : `${days} day${days === 1 ? "" : "s"} remaining`}
                      </div>
                      <div className={`text-xs mt-0.5 ${overdue ? "text-red-700" : "text-rose-700"}`}>
                        Deadline: {deadline.toLocaleDateString(undefined, { dateStyle: "full" })} · Client must pay final 50% within 14 days of entering Final Review.
                      </div>
                      {project.final_payment_url && (
                        <a href={project.final_payment_url} target="_blank" rel="noopener noreferrer" data-testid="link-final-payment"
                          className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold ${overdue ? "text-red-800" : "text-rose-800"} hover:underline`}>
                          <ExternalLink size={11} /> View Stripe payment link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Pipeline visualization */}
            <div className="flex items-center gap-1 overflow-x-auto py-1" data-testid="status-pipeline-viz">
              {["pending", "ai_magicians", "human_review", "pending_feedback", "applying_feedback", "final_review", "delivered"].map((s, i, arr) => {
                const currentIdx = arr.indexOf(project.status);
                const done = currentIdx >= 0 && i <= currentIdx;
                const active = i === currentIdx;
                return (
                  <div key={s} className="flex items-center gap-1 flex-shrink-0">
                    <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${
                      active ? "bg-zinc-950 text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-400"
                    }`}>{STATUS_LABELS[s]}</div>
                    {i < arr.length - 1 && <div className={`w-2 h-px ${done && i < currentIdx ? "bg-emerald-300" : "bg-zinc-200"}`} />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview URL */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="preview-url-card">
            <div className="flex items-center gap-2 mb-3">
              <LinkIcon size={16} className="text-zinc-700" />
              <h3 className="font-semibold text-zinc-900 text-sm">Preview URL</h3>
              {project.preview_url && <a href={project.preview_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-blue-600 inline-flex items-center gap-1 hover:underline" data-testid="link-preview-open"><ExternalLink size={11} /> Open</a>}
            </div>
            <div className="flex gap-2">
              <input data-testid="input-preview-url" type="url" value={previewUrl} onChange={e => setPreviewUrl(e.target.value)}
                placeholder="https://your-mvp.preview.example.com"
                className="flex-1 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              <button data-testid="btn-save-preview-url" onClick={handleSavePreview} disabled={savingPreview || previewUrl === (project.preview_url || "")}
                className="px-4 py-2.5 bg-zinc-950 text-white text-sm font-semibold rounded-xl hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1.5">
                {savingPreview ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save</>}
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">When saved, you'll be asked whether to email the client a notification.</p>
          </div>

          {/* Generate Emergent Prompt */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid="emergent-prompt-card">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Bot size={16} className="text-zinc-700" />
                <h3 className="font-semibold text-zinc-900 text-sm">Emergent Build Prompt</h3>
                {project.emergent_prompt && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Ready</span>}
              </div>
              <button data-testid="btn-generate-emergent-prompt" onClick={handleGenerate} disabled={generating}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white text-xs font-semibold rounded-full hover:bg-purple-700 disabled:opacity-50">
                {generating ? <><Loader2 size={12} className="animate-spin" /> Generating with GPT-5.2…</> : <><Sparkles size={12} /> {project.emergent_prompt ? "Regenerate" : "Generate"} with GPT</>}
              </button>
            </div>
            {genError && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3" data-testid="emergent-prompt-error">{genError}</div>}
            {project.emergent_prompt ? (
              <div>
                <div className="flex items-center justify-between mb-2 text-xs text-zinc-500">
                  <span>Generated {fmtDateTime(project.emergent_prompt_generated_at)} · {project.emergent_prompt_model}</span>
                  <CopyButton text={project.emergent_prompt} label="Copy prompt" />
                </div>
                <pre data-testid="emergent-prompt-output" className="bg-zinc-950 text-zinc-100 text-[12px] leading-relaxed font-mono p-4 rounded-xl overflow-auto whitespace-pre-wrap max-h-96">{project.emergent_prompt}</pre>
              </div>
            ) : (
              <p className="text-sm text-zinc-500">Generate a ready-to-paste Emergent prompt that combines the client idea with the AI proposal — paste it into Emergent to scaffold the base app.</p>
            )}
          </div>

          {/* Additional Charges */}
          <AdditionalChargesAdminCard projectId={projectId} currency={project.currency} onChange={load} />

          {/* Quote versions */}
          <QuoteVersionsCard projectId={projectId} currentProposalId={proposal?.proposal_id} onRegenerated={load} />

          {/* Deliverables / Notes (file uploads) */}
          <DeliverablesCard projectId={projectId} />

          {/* Client intake PDFs (attached at quote request time) */}
          <IntakePdfsCard projectId={projectId} />

          {/* Client change requests (post-payment scope changes) */}
          <ChangeRequestsAdminCard projectId={projectId} />

          {/* Team assignment */}
          <AssignTeamCard project={project} onChange={load} />

          {/* Start Date */}
          <StartDateCard project={project} onChange={load} />

          {/* Saved Prompts (admin-only) */}
          <CollapsibleCard title="Saved Prompts (admin-only)" icon={Code} testId="saved-prompts-card" badge="internal">
            <div className="space-y-4 mt-4">
              {proposal?.generation_prompt && (
                <div>
                  <div className="flex items-center justify-between mb-2"><div className="text-xs font-semibold text-zinc-700">Proposal generation prompt (Claude)</div><CopyButton text={proposal.generation_prompt} /></div>
                  <pre className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-[11px] leading-relaxed font-mono p-3 rounded-xl overflow-auto whitespace-pre-wrap max-h-64">{proposal.generation_prompt}</pre>
                </div>
              )}
              {proposal?.raw_ai_response && (
                <div>
                  <div className="flex items-center justify-between mb-2"><div className="text-xs font-semibold text-zinc-700">Claude raw response (parsed JSON)</div><CopyButton text={proposal.raw_ai_response} /></div>
                  <pre className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-[11px] leading-relaxed font-mono p-3 rounded-xl overflow-auto whitespace-pre-wrap max-h-64">{proposal.raw_ai_response}</pre>
                </div>
              )}
              {project.emergent_prompt_meta_prompt && (
                <div>
                  <div className="flex items-center justify-between mb-2"><div className="text-xs font-semibold text-zinc-700">Emergent meta-prompt sent to GPT-5.2</div><CopyButton text={project.emergent_prompt_meta_prompt} /></div>
                  <pre className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-[11px] leading-relaxed font-mono p-3 rounded-xl overflow-auto whitespace-pre-wrap max-h-64">{project.emergent_prompt_meta_prompt}</pre>
                </div>
              )}
              {!proposal?.generation_prompt && !project.emergent_prompt_meta_prompt && (
                <p className="text-sm text-zinc-500">No saved prompts yet.</p>
              )}
            </div>
          </CollapsibleCard>

          {/* AI Proposal Data — admin-only fields visible */}
          {proposal && (
            <CollapsibleCard title="AI Proposal Data" icon={FileText} testId="ai-proposal-data" badge={`score ${proposal.complexity_score}`}>
              <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                <div className="bg-zinc-50 rounded-lg p-3"><div className="text-zinc-500">Complexity</div><div className="font-bold text-zinc-900 text-base">{proposal.complexity_score} pts</div></div>
                <div className="bg-zinc-50 rounded-lg p-3"><div className="text-zinc-500">Base price</div><div className="font-bold text-zinc-900 text-base">${proposal.base_price_usd?.toFixed(0)}</div></div>
                <div className="bg-zinc-50 rounded-lg p-3"><div className="text-zinc-500">Final (×4)</div><div className="font-bold text-zinc-900 text-base">${proposal.final_price_usd?.toFixed(0)}</div></div>
                <div className="bg-zinc-50 rounded-lg p-3"><div className="text-zinc-500">Adjusted</div><div className="font-bold text-zinc-900 text-base">${proposal.delivery_adjusted_price_usd?.toFixed(0)}</div></div>
              </div>
              <div className="mt-4">
                <div className="text-xs font-semibold text-zinc-700 mb-2">Components (with points)</div>
                <ul className="space-y-1.5 text-sm">
                  {(proposal.components_identified || []).map((c, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="text-[10px] font-bold bg-zinc-200 text-zinc-700 px-1.5 py-0.5 rounded-full">{c.points}pt</span><span><strong>{c.name}</strong> — <span className="text-zinc-600">{c.description}</span></span></li>
                  ))}
                </ul>
              </div>
              <div className="mt-4">
                <div className="text-xs font-semibold text-zinc-700 mb-2">Tech stack</div>
                <div className="flex flex-wrap gap-1.5">{(proposal.tech_stack || []).map((t, i) => <span key={i} className="text-xs bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full">{t}</span>)}</div>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div><div className="font-semibold text-zinc-700 mb-1">Assumptions</div><ul className="space-y-1 text-zinc-600 list-disc list-inside">{(proposal.assumptions || []).map((a, i) => <li key={i}>{a}</li>)}</ul></div>
                <div><div className="font-semibold text-zinc-700 mb-1">Exclusions</div><ul className="space-y-1 text-zinc-600 list-disc list-inside">{(proposal.exclusions || []).map((a, i) => <li key={i}>{a}</li>)}</ul></div>
              </div>
            </CollapsibleCard>
          )}
        </div>

        {/* Right column: chat */}
        <div className="lg:col-span-1">
          <ChatPanel projectId={projectId} />
        </div>
      </div>

      <NotifyModal
        open={showNotify}
        onClose={() => { setShowNotify(false); setPendingPreview(null); }}
        onConfirm={confirmSavePreview}
        message="The preview URL will be saved. Do you want to email the client a notification asking them to check their project page?"
      />
    </div>
  );
}
